import { SecondaryRegularButton } from 'components/ReusableComponents/Button/RegularButtons'
import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import SearchTextInput from 'components/ReusableComponents/SearchTextInput'
import Table, { TitleCell, TitleRow } from 'components/ReusableComponents/Table'
import Toggle from 'components/ReusableComponents/Toggle'
import ManageUserDialog from 'features/ManageUserDialog'
import UserRow from 'features/Users/UserRow'
import useCopyText from 'hooks/useCopyText'
import moment from 'moment'
import { useEffect, useState } from 'react'
import userApi from 'services/api/user'
import downloadAsCsv from 'utils/downloadAsCsv'

import { ReactComponent as ExcelIcon } from './excel.svg'
import styles from './Users.module.scss'

const Users = () => {
  const [users, setUsers] = useState()
  const [error, setError] = useState()
  const [isFetchingUsers, setIsFetchingUsers] = useState(false)
  const [isShowingInactiveUsers, setIsShowingInactiveUsers] = useState(false)

  const [showingEditUserDialog, setShowingEditUserDialog] = useState()

  const [userToBeEditedId, setUserToBeEditedId] = useState()

  const [searchString, setSearchSting] = useState('')

  const copy = useCopyText()

  function handleClickDownloadForHubspot() {
    const titleRow = ['First Name', 'Last Name', 'Email', 'Phone']

    const resAsArrays = users
      .filter(u => !u.isDeleted)
      .map(b => [b.fullName.split(' ')[0], b.fullName.split(' ')[1], b.email, b.phone])

    const dataArray = [titleRow, ...resAsArrays]
    const fileName = `dashboard-users-for-hubspot.csv`

    downloadAsCsv(dataArray, fileName)
  }

  function handleClickDownloadForSendgrid() {
    const titleRow = [
      'email',
      'first_name',
      'last_name',
      'address_line_1',
      'address_line_2',
      'city',
      'state_province_region',
      'postal_code',
      'country'
    ]

    const resAsArrays = users
      .filter(u => !u.isDeleted)
      .map(b => [
        b?.email,
        b.fullName.split(' ')[0],
        b.fullName.split(' ')[1],
        b?.address,
        '',
        b?.city,
        b?.state,
        b?.postalCode,
        b?.countryCode
      ])

    const dataArray = [titleRow, ...resAsArrays]
    const fileName = `dashboard-users-for-sendgrid.csv`

    downloadAsCsv(dataArray, fileName)
  }

  useEffect(() => {
    async function fetchUsers() {
      setIsFetchingUsers(true)

      setError(null)

      userApi
        .allUsers()
        .then(res => {
          setUsers(res.users)
        })
        .catch(err => {
          setError(err)
        })
        .finally(() => {
          setIsFetchingUsers(false)
        })
    }

    if (!userToBeEditedId) {
      fetchUsers()
    }
  }, [userToBeEditedId])

  async function handleAddedUserSuccess() {
    setShowingEditUserDialog(false)
    setUserToBeEditedId(null)
  }

  function handleEditButtonClick(userId) {
    setUserToBeEditedId(userId)
    setShowingEditUserDialog(true)
  }

  const filteredUsers = users
    ?.filter(u =>
      [u.fullName, u.email, u.phone].join(' ').match(new RegExp(searchString, 'i'))
    )
    ?.filter(u => (isShowingInactiveUsers ? true : !u.isInActive))

  function handleClickDownload() {
    const titleRow = [
      'User name',
      'Phone number',
      'Address',
      'City',
      'Country',
      'Email',
      'Postal code',
      'State',
      'Timezone',
      'Merchant name',
      'Hotels',
      'Position',
      'UTC offset',
      'Added on',
      'User id',
      'Deleted'
    ]

    const resAsArrays = filteredUsers.map(u => [
      u.fullName,
      u.phone,
      u.address,
      u.city,
      u.countryCode,
      u.email,
      u.postalCode,
      u.state,
      u.tzTimeZoneName,
      u.merchantName,
      u.subsidiaries?.map(s => s.subsidiaryName).join(', '),
      [...new Set(u.subsidiaries?.filter(s => s.position).map(s => s.position))].join(
        ', '
      ),
      u.utcOffSet,
      moment(u.addedOn).format('YYYY/MM/DD'),
      u.userId,
      u.isDeleted
    ])

    const dataArray = [titleRow, ...resAsArrays]
    const fileName = `user_list.csv`

    downloadAsCsv(dataArray, fileName)
  }

  const filteredEmails = [
    ...new Set(filteredUsers?.map(u => u.email.replace(/\+[^@]+/gm, '')))
  ]

  return (
    <div>
      {showingEditUserDialog && (
        <ManageUserDialog
          userToEdit={{
            ...users.find(user => user.userId === userToBeEditedId),
            name: users.find(user => user.userId === userToBeEditedId).fullName
          }}
          showPosition={false}
          handleClose={() => setShowingEditUserDialog(false)}
          handleAddedUser={handleAddedUserSuccess}
        />
      )}
      <CustomHeader
        title={`Dashboard Users ${
          filteredUsers?.length ? `(${filteredUsers.length})` : ''
        }`}
        width={1180}
      />
      <div className={styles.filters}>
        <div className={styles.searchInputHolder}>
          <SearchTextInput
            placeholder="Type full name, email or phone"
            handleChange={value => setSearchSting(value)}
            value={searchString}
          />
          <span className={styles.seperator} />
          <div className={styles.toggleHolder}>
            <Toggle
              checked={isShowingInactiveUsers}
              handleChange={e => setIsShowingInactiveUsers(e.target.checked)}
              label="Show inactive"
            />
          </div>
          <button
            className={styles.miniButton}
            onClick={() =>
              copy(
                filteredEmails.join('; '),
                `copied ${filteredEmails.length} emails`,
                false
              )
            }
          >
            Copy all emails
          </button>
          <button className={styles.miniButton} onClick={handleClickDownloadForHubspot}>
            Export for Hubspot
          </button>
          <button className={styles.miniButton} onClick={handleClickDownloadForSendgrid}>
            Export for Sendgrid
          </button>
          <SecondaryRegularButton
            className={styles.copyToExcelButton}
            onClick={() => handleClickDownload()}
          >
            <ExcelIcon /> Download as CSV
          </SecondaryRegularButton>
        </div>
      </div>
      {error && <div>{error}</div>}
      {isFetchingUsers && <LoadingIndicator />}
      {!isFetchingUsers && users && (
        <Table widths={[190, 220, 130, 90, 250, 150, 70, 60]}>
          <TitleRow>
            <TitleCell>Full name</TitleCell>
            <TitleCell>Email</TitleCell>
            <TitleCell>Phone</TitleCell>
            <TitleCell>Timezone</TitleCell>
            <TitleCell>Hotels</TitleCell>
            <TitleCell>Added on</TitleCell>
            <TitleCell>User id</TitleCell>
            <TitleCell></TitleCell>
          </TitleRow>
          {filteredUsers.map(u => (
            <>
              <UserRow
                {...u}
                key={u.userId}
                onEditClick={() => handleEditButtonClick(u.userId)}
              />
            </>
          ))}
        </Table>
      )}
    </div>
  )
}

export default Users
